import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../../lib/supabaseClient';
import ActiveJobs from './components/ActiveJobs';
import JobHistory from './components/JobHistory';
import ProfileSettings from './components/ProfileSettings';
import Notifications from './components/Notifications';
import PaymentMethods from './components/PaymentMethods';
import FavoriteProviders from './components/FavoriteProviders';

export default function ClientDashboardPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('active-jobs');
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Set document title and meta tags
    document.title = 'Customer Dashboard | Manage Your Bookings - Taskbudy';
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Manage your Taskbudy bookings. View active jobs, booking history, favorite providers, payment methods, and account settings.');
    }
    const metaRobots = document.querySelector('meta[name="robots"]');
    if (metaRobots) {
      metaRobots.setAttribute('content', 'noindex, nofollow');
    }
  }, []);

  useEffect(() => {
    checkAuth();
    fetchUnreadNotifications();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        navigate('/auth/signin?redirect=/client-dashboard');
        return;
      }

      // Fetch user details
      const { data: userData } = await supabase
        .from('users')
        .select('*')
        .eq('id', user.id)
        .single();

      setUser(userData);
    } catch (error) {
      console.error('Auth error:', error);
      navigate('/auth/signin');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnreadNotifications = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('client_notifications')
        .select('id')
        .eq('user_id', user.id)
        .eq('is_read', false);

      if (!error && data) {
        setUnreadCount(data.length);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-blue-600 animate-spin"></i>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen bg-gray-50 overflow-x-hidden">
        {/* Top Navigation */}
        <nav className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/" className="flex items-center">
                <img 
                  src="https://static.readdy.ai/image/a3382d87126fc476a032aa8f5ebcbb38/dc77c2be5a78759d16f1acf02198b697.png" 
                  alt="TaskBudy" 
                  className="h-8 md:h-10"
                />
              </Link>
              <div className="flex items-center gap-2 sm:gap-4">
                <button
                  onClick={() => navigate('/service-request')}
                  className="px-3 sm:px-4 py-2 bg-gradient-to-r from-[#FF6A00] to-[#FF8533] text-white rounded-lg hover:shadow-lg transition-all whitespace-nowrap cursor-pointer font-semibold text-xs sm:text-sm"
                >
                  <i className="ri-add-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">Book Service</span>
                  <span className="sm:hidden">Book</span>
                </button>
                <button
                  onClick={handleSignOut}
                  className="px-3 sm:px-4 py-2 text-gray-700 hover:text-gray-900 transition-colors whitespace-nowrap cursor-pointer text-xs sm:text-sm"
                >
                  <i className="ri-logout-box-line mr-1 sm:mr-2"></i>
                  <span className="hidden sm:inline">Sign Out</span>
                </button>
              </div>
            </div>
          </div>
        </nav>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          {/* Welcome Header */}
          <div className="bg-gradient-to-r from-[#0B3C5D] to-[#0B3C5D] rounded-xl sm:rounded-2xl p-6 sm:p-8 mb-6 sm:mb-8 text-white">
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Welcome back, {user?.full_name || 'Client'}!</h1>
            <p className="text-sm sm:text-base text-blue-100">Manage your bookings and track your service providers</p>
          </div>

          {/* Tabs Navigation */}
          <div className="bg-white rounded-xl shadow-sm mb-4 sm:mb-6 overflow-x-auto">
            <div className="flex border-b border-gray-200 min-w-max sm:min-w-0">
              <button
                onClick={() => setActiveTab('active-jobs')}
                className={`px-4 sm:px-6 py-3 sm:py-4 font-semibold whitespace-nowrap cursor-pointer transition-colors text-xs sm:text-sm ${
                  activeTab === 'active-jobs'
                    ? 'border-b-2 border-[#FF6A00] text-[#FF6A00]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-time-line mr-1 sm:mr-2"></i>
                Active Jobs
              </button>
              <button
                onClick={() => setActiveTab('favorites')}
                className={`px-4 sm:px-6 py-3 sm:py-4 font-semibold whitespace-nowrap cursor-pointer transition-colors text-xs sm:text-sm ${
                  activeTab === 'favorites'
                    ? 'border-b-2 border-[#FF6A00] text-[#FF6A00]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-heart-line mr-1 sm:mr-2"></i>
                Favorites
              </button>
              <button
                onClick={() => setActiveTab('history')}
                className={`px-4 sm:px-6 py-3 sm:py-4 font-semibold whitespace-nowrap cursor-pointer transition-colors text-xs sm:text-sm ${
                  activeTab === 'history'
                    ? 'border-b-2 border-[#FF6A00] text-[#FF6A00]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-history-line mr-1 sm:mr-2"></i>
                History
              </button>
              <button
                onClick={() => {
                  setActiveTab('notifications');
                  setUnreadCount(0);
                }}
                className={`px-4 sm:px-6 py-3 sm:py-4 font-semibold whitespace-nowrap cursor-pointer transition-colors relative text-xs sm:text-sm ${
                  activeTab === 'notifications'
                    ? 'border-b-2 border-[#FF6A00] text-[#FF6A00]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-notification-line mr-1 sm:mr-2"></i>
                <span className="hidden sm:inline">Notifications</span>
                <span className="sm:hidden">Notifs</span>
                {unreadCount > 0 && (
                  <span className="absolute top-1 sm:top-2 right-1 sm:right-2 w-4 h-4 sm:w-5 sm:h-5 bg-red-500 text-white text-[10px] sm:text-xs rounded-full flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>
              <button
                onClick={() => setActiveTab('payments')}
                className={`px-4 sm:px-6 py-3 sm:py-4 font-semibold whitespace-nowrap cursor-pointer transition-colors text-xs sm:text-sm ${
                  activeTab === 'payments'
                    ? 'border-b-2 border-[#FF6A00] text-[#FF6A00]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-bank-card-line mr-1 sm:mr-2"></i>
                <span className="hidden sm:inline">Payment Methods</span>
                <span className="sm:hidden">Payments</span>
              </button>
              <button
                onClick={() => setActiveTab('profile')}
                className={`px-4 sm:px-6 py-3 sm:py-4 font-semibold whitespace-nowrap cursor-pointer transition-colors text-xs sm:text-sm ${
                  activeTab === 'profile'
                    ? 'border-b-2 border-[#FF6A00] text-[#FF6A00]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="ri-user-line mr-1 sm:mr-2"></i>
                Profile
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div>
            {activeTab === 'active-jobs' && <ActiveJobs />}
            {activeTab === 'favorites' && <FavoriteProviders />}
            {activeTab === 'history' && <JobHistory />}
            {activeTab === 'notifications' && <Notifications onUnreadChange={setUnreadCount} />}
            {activeTab === 'payments' && <PaymentMethods />}
            {activeTab === 'profile' && <ProfileSettings user={user} onUpdate={setUser} />}
          </div>
        </div>
      </div>
    </>
  );
}
